import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';

interface SeriesPoint { date: string; value: number }

export default function MarketingAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visits, setVisits] = useState<SeriesPoint[]>([]);
  const [topPages, setTopPages] = useState<{ label: string; value: number }[]>([]);
  const [referrers, setReferrers] = useState<{ label: string; value: number }[]>([]);
  const [cta, setCta] = useState<SeriesPoint[]>([]);
  const [uniqueVisitors, setUniqueVisitors] = useState<SeriesPoint[]>([]);
  const [funnel, setFunnel] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const call = async (action: string, params?: Record<string, any>) => {
          const { data, error } = await supabase.functions.invoke('posthog-analytics', { body: { action, params } });
          if (error) throw error;
          return data?.data;
        };

        const [trends, pages, refs, ctaClicks, uniques, funnelData] = await Promise.all([
          call('trends', { date_from: '-30d' }),
          call('pages', { date_from: '-30d' }),
          call('referrers', { date_from: '-30d' }),
          call('events', { event: 'marketing_cta_click', date_from: '-30d' }),
          call('unique_visitors', { date_from: '-30d' }),
          call('funnel', { date_from: '-30d' }),
        ]);

        const toSeries = (insight: any): SeriesPoint[] => {
          const res = (insight?.result || insight?.results || insight?.data || insight || []) as any;
          const series = (Array.isArray(res?.[0]?.data) ? res[0] : res);
          if (!series?.data || !series?.labels) return [];
          return series.labels.map((d: string, i: number) => ({ date: d, value: series.data[i] || 0 }));
        };

        const toBreakdown = (insight: any): { label: string; value: number }[] => {
          const res = (insight?.result || insight?.results || insight?.data || insight || []) as any;
          const rows = Array.isArray(res) ? res : [];
          return rows.slice(0, 10).map((row: any) => ({ label: row.label || row.breakdown_value || row['$current_url'] || row['$referrer'] || 'unknown', value: row.aggregated_value || row.count || 0 }));
        };

        setVisits(toSeries(trends));
        setTopPages(toBreakdown(pages));
        setReferrers(toBreakdown(refs));
        setCta(toSeries(ctaClicks));
        setUniqueVisitors(toSeries(uniques));
        setFunnel(funnelData);
      } catch (e: any) {
        setError(e?.message || 'Failed to load marketing analytics');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <DashboardLayout title="Marketing Analytics" description="Top‑of‑funnel metrics (via PostHog Edge Function)" maxWidth="7xl">
      {error && <div className="text-red-600 text-sm">{error}</div>}

      {/* KPI row */}
      <KpiRow
        loading={loading}
        visits={visits}
        uniques={uniqueVisitors}
        cta={cta}
        funnel={funnel}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader><CardTitle>Visitors & Pageviews (30d)</CardTitle></CardHeader>
            <CardContent>
              {loading ? 'Loading…' : (
                <ul className="text-sm space-y-1">
                  {visits.slice(-10).map((p) => (
                    <li key={p.date} className="flex justify-between"><span>{p.date}</span><span>{Math.round(p.value)}</span></li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Unique Visitors (30d)</CardTitle></CardHeader>
            <CardContent>
              {loading ? 'Loading…' : (
                <ul className="text-sm space-y-1">
                  {uniqueVisitors.slice(-10).map((p) => (
                    <li key={p.date} className="flex justify-between"><span>{p.date}</span><span>{Math.round(p.value)}</span></li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Top Landing Pages</CardTitle></CardHeader>
            <CardContent>
              {loading ? 'Loading…' : (
                <ul className="text-sm space-y-1">
                  {topPages.map((p) => (
                    <li key={p.label} className="flex justify-between truncate"><span className="truncate max-w-[70%]" title={p.label}>{p.label}</span><span>{Math.round(p.value)}</span></li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Top Referrers</CardTitle></CardHeader>
            <CardContent>
              {loading ? 'Loading…' : (
                <ul className="text-sm space-y-1">
                  {referrers.map((r) => (
                    <li key={r.label} className="flex justify-between truncate"><span className="truncate max-w-[70%]" title={r.label}>{r.label}</span><span>{Math.round(r.value)}</span></li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>CTA Clicks (30d)</CardTitle></CardHeader>
            <CardContent>
              {loading ? 'Loading…' : (
                <ul className="text-sm space-y-1">
                  {cta.slice(-10).map((p) => (
                    <li key={p.date} className="flex justify-between"><span>{p.date}</span><span>{Math.round(p.value)}</span></li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader><CardTitle>Acquisition Funnel (30d)</CardTitle></CardHeader>
            <CardContent>
              {loading ? 'Loading…' : (
                <div className="text-sm">
                  {Array.isArray(funnel?.result?.[0]?.count) || Array.isArray(funnel?.result?.[0]?.steps)
                    ? (
                      <ul className="space-y-1">
                        {(funnel?.result?.[0]?.steps || funnel?.result || [])
                          .map((s: any, idx: number) => (
                            <li key={idx} className="flex justify-between">
                              <span>{s.name || s.custom_name || s.event || `Step ${idx+1}`}</span>
                              <span>{Math.round(s.count || s.aggregated_value || 0)}</span>
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <div className="text-muted-foreground">No funnel data</div>
                    )}
                </div>
              )}
            </CardContent>
          </Card>
      </div>
    </DashboardLayout>
  );
}

function KpiRow({ loading, visits, uniques, cta, funnel }: { loading: boolean; visits: SeriesPoint[]; uniques: SeriesPoint[]; cta: SeriesPoint[]; funnel: any }) {
  const sum = (arr: SeriesPoint[]) => arr.reduce((acc, p) => acc + (Number(p?.value) || 0), 0);
  const pageviews30d = sum(visits);
  const uniqueVisitors30d = sum(uniques);
  const ctaClicks30d = sum(cta);
  const ctr = pageviews30d > 0 ? (ctaClicks30d / pageviews30d) : 0;

  const steps: any[] = Array.isArray(funnel?.result?.[0]?.steps)
    ? funnel?.result?.[0]?.steps
    : (Array.isArray(funnel?.result) ? funnel?.result : []);

  const findCount = (...labels: string[]) => {
    const step = steps.find((s) => labels.some((l) => s?.name === l || s?.custom_name === l || s?.event === l));
    return Number(step?.count ?? step?.aggregated_value ?? 0);
  };

  const signups = findCount('Sign Up', 'sign_up');
  const activations = findCount('First Chat', 'first_chat');
  const signupRate = uniqueVisitors30d > 0 ? (signups / uniqueVisitors30d) : 0;
  const activationRate = signups > 0 ? (activations / signups) : 0;

  const fmt = (n: number) => new Intl.NumberFormat().format(Math.round(n));
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <StatCard title="Pageviews (30d)" value={loading ? '…' : fmt(pageviews30d)} />
      <StatCard title="Unique Visitors (30d)" value={loading ? '…' : fmt(uniqueVisitors30d)} />
      <StatCard title="CTA Clicks (30d)" value={loading ? '…' : fmt(ctaClicks30d)} />
      <StatCard title="CTA CTR" value={loading ? '…' : pct(ctr)} />
      <StatCard title="Signups (30d)" value={loading ? '…' : fmt(signups)} />
      <StatCard title="Activation" value={loading ? '…' : pct(activationRate)} />
    </div>
  );
}

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">{title}</CardTitle></CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold tracking-tight">{value}</div>
      </CardContent>
    </Card>
  );
}
